import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Alert, CircularProgress } from "@mui/material";
import { useActiveTenant } from "../../../hooks/useActiveTenant";
import {
  uploadGeminiImport, getGeminiImportRowStatus, listGeminiImportRows, confirmGeminiImport, discardGeminiImport, updateGeminiImportRow, createDocumentBankItem,
} from "../../../api/backend";
import { W, STEP_ORDER } from "./wizardTokens";
import PickStep from "./PickStep";
import ScanStep from "./ScanStep";
import ReviewStep from "./ReviewStep";
import { parseDraft, serializeDraft, computeMissingFields, countUnresolvedFlags } from "./reviewModel";
import DocUploadStep from "./DocUploadStep";

function titleFromFileName(name) {
  return name.replace(/\.[^./]+$/, "");
}

const POLL_INTERVAL_MS = 1200;
const POLL_TIMEOUT_MS = 90000;
// Extra sheets split out of a multi-sheet file queue behind a small backend worker pool, so they
// can legitimately take much longer than a lone file to start and finish.
const SIBLING_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Waits out the backend's real 3-pass extraction pipeline (STRUCTURE -> SCORING -> REVIEW),
 * reporting each poll's row (its phase, plus whatever it has extracted so far) via onProgress,
 * until the row leaves PROCESSING.
 */
async function pollUntilDone(batchId, rowId, onProgress, timeoutMs = POLL_TIMEOUT_MS) {
  const start = Date.now();
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const row = await getGeminiImportRowStatus(batchId, rowId);
    if (row.status !== "PROCESSING") return row;
    onProgress(row);
    if (Date.now() - start > timeoutMs) {
      throw new Error("This is taking longer than expected. Try again.");
    }
  }
}

/**
 * A file holding several skill sheets is split by the backend into one staged row per sheet (same
 * batch + file name). The upload only returns the first, so find the rest and wait for them too.
 * A lookup failure remains visible so a multi-sheet file is never silently treated as complete.
 */
async function collectSiblingRows(batchId, primaryRow, seenIds) {
  const all = (await listGeminiImportRows(batchId)) || [];
  const siblings = all.filter((r) => r.id !== primaryRow.id && r.fileName === primaryRow.fileName && !seenIds.has(r.id));
  return Promise.all(siblings.map(async (r) => {
    if (r.status !== "PROCESSING") return r;
    try {
      return await pollUntilDone(batchId, r.id, () => {}, SIBLING_POLL_TIMEOUT_MS);
    } catch (err) {
      return { ...r, status: "FAILED", errorMessage: err?.message || "Scan failed." };
    }
  }));
}

function Stepper({ kind, step }) {
  const order = STEP_ORDER[kind];
  const idx = order.indexOf(step);
  return (
    <Box sx={{ display: "flex", gap: "6px", alignItems: "center", mt: "14px", fontSize: "12.5px", fontWeight: 600, color: W.ink3, flexWrap: "wrap" }}>
      {order.map((k, i) => (
        <React.Fragment key={k}>
          {i > 0 && <Box sx={{ width: 24, height: 1, bgcolor: W.line }} />}
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: i === idx ? W.ink : "inherit" }}>
            <Box sx={{ width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "11px", border: `1px solid ${W.line}`, bgcolor: i < idx ? W.success : i === idx ? W.primary : "transparent", borderColor: i < idx ? W.success : i === idx ? W.primary : W.line, color: i <= idx ? "#fff" : "inherit" }}>
              {i < idx ? "✓" : i + 1}
            </Box>
            {k === "pick" ? "Choose files" : k === "scan" ? "AI scan" : k === "review" ? "Review" : "Upload"}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

export default function ImportWizard({ open, onClose, onImported }) {
  const { tenantId, user } = useActiveTenant();
  const storageKey = `tc.skill-import:${tenantId}:${user?.id || "current"}`;
  const [resumeBatchId, setResumeBatchId] = useState(null);
  useEffect(() => {
    if (open && tenantId) setResumeBatchId(sessionStorage.getItem(storageKey));
  }, [open, tenantId, storageKey]);

  const [step, setStep] = useState("pick");
  const [kind, setKind] = useState("ai");
  const [aiType, setAiType] = useState("skill-sheet");
  const [files, setFiles] = useState([]);

  const [batchId, setBatchId] = useState(null);
  const [scanQueue, setScanQueue] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [sel, setSel] = useState(0);

  const [docQueue, setDocQueue] = useState([]);

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmedCount, setConfirmedCount] = useState(null);

  const reset = () => {
    setStep("pick"); setKind("ai"); setAiType("skill-sheet"); setFiles([]);
    setBatchId(null); setScanQueue([]); setDrafts([]); setSel(0);
    setDocQueue([]); setConfirming(false); setConfirmError(null); setConfirmedCount(null);
  };

  const handleClose = (imported, discard = false) => {
    if (batchId && discard) discardGeminiImport(batchId).catch(() => {});
    if (discard) sessionStorage.removeItem(storageKey);
    reset();
    onClose?.(imported);
  };

  /**
   * Uploads one file and waits for every staged row it produced (one per skill sheet found in it).
   * Returns the batch id and all of its rows; updates the file's queue entry as it goes.
   */
  const scanFile = async (index, batchIdIn, seenIds, resumeRow = null) => {
    const patch = (fields) => setScanQueue((q) => q.map((item, idx) => (idx === index ? { ...item, ...fields } : item)));
    patch({ status: "uploading", error: null, phase: "STRUCTURE", partial: null });
    let uploadedRow = resumeRow;
    try {
      let row = resumeRow ? await getGeminiImportRowStatus(resumeRow.batchId, resumeRow.id) : await uploadGeminiImport(aiType, files[index], batchIdIn);
      uploadedRow = row;
      const currentBatchId = row.batchId;
      setBatchId(currentBatchId);
      sessionStorage.setItem(storageKey, currentBatchId);
      patch({ row });
      if (row.status === "PROCESSING") {
        row = await pollUntilDone(currentBatchId, row.id, (progress) => patch({ phase: progress.phase, partial: progress.payload }));
      }
      const siblings = aiType === "skill-sheet" ? await collectSiblingRows(currentBatchId, row, seenIds) : [];
      const rows = [row, ...siblings];
      rows.forEach((r) => seenIds.add(r.id));

      const pending = rows.filter((r) => r.status === "PENDING");
      const failedCount = rows.length - pending.length;
      const status = pending.length === 0 ? "failed" : pending.every((r) => r.isDuplicate) ? "duplicate" : "ok";
      const error = pending.length === 0
        ? rows.find((r) => r.errorMessage)?.errorMessage ?? null
        : failedCount > 0 ? `${failedCount} of ${rows.length} skill sheets failed to scan` : row.errorMessage || null;
      patch({ status, error, row, sheetCount: rows.length });
      return { batchId: currentBatchId, rows };
    } catch (err) {
      patch({ status: "failed", error: err?.response?.data?.error || err?.message || "Upload failed." });
      return { batchId: uploadedRow?.batchId || batchIdIn, rows: [] };
    }
  };

  const runAiScan = async () => {
    const queue = files.map((file) => ({ file, status: "waiting" }));
    setScanQueue(queue);
    setStep("scan");

    let currentBatchId = batchId;
    const rows = [];
    const seenIds = new Set();
    for (let i = 0; i < files.length; i++) {
      const result = await scanFile(i, currentBatchId, seenIds);
      currentBatchId = result.batchId;
      rows.push(...result.rows);
    }
    setBatchId(currentBatchId);

    const pendingRows = rows.filter((r) => r.status === "PENDING");
    setDrafts(pendingRows.map(parseDraft));
    setSel(0);
  };

  const retryFile = async (index) => {
    const seenIds = new Set(drafts.map((d) => d.rowId));
    const priorRow = scanQueue[index]?.row;
    if ((!files[index] || !(files[index] instanceof File)) && priorRow?.status === "FAILED") {
      setConfirmError("This scan could not finish. Review any extracted draft, or discard the import and select the original file again.");
      return;
    }
    const result = await scanFile(index, batchId, seenIds, priorRow && priorRow.status !== "FAILED" ? priorRow : null);
    if (!batchId && result.batchId) setBatchId(result.batchId);
    const pendingRows = result.rows.filter((r) => r.status === "PENDING");
    if (pendingRows.length > 0) setDrafts((prev) => [...prev, ...pendingRows.filter((r) => !prev.some((d) => d.rowId === r.id)).map(parseDraft)]);
  };

  const resumeScan = async () => {
    setConfirmError(null);
    try {
      const rows = (await listGeminiImportRows(resumeBatchId)) || [];
      setBatchId(resumeBatchId);
      const live = rows.filter((r) => !["CONFIRMED", "DISCARDED"].includes(r.status));
      if (live.length === 0) {
        sessionStorage.removeItem(storageKey);
        setResumeBatchId(null);
        if (rows.some((r) => r.status === "CONFIRMED")) {
          setConfirmedCount(rows.filter((r) => r.status === "CONFIRMED").length);
          setStep("review");
          onImported?.();
        }
        return;
      }
      setAiType(live[0]?.type === "TEST" ? "test" : "skill-sheet");
      setKind("ai");
      setDrafts(live.filter((r) => r.status === "PENDING" || (r.status === "FAILED" && r.payload)).map((r) => ({ ...parseDraft(r), dirty: r.status === "FAILED" })));
      setScanQueue(live.map((r) => ({ file: { name: r.fileName }, row: r, status: r.status === "PENDING" ? "ok" : "failed", error: r.status === "PROCESSING" ? "Still processing. Check again to reconnect." : r.errorMessage })));
      setStep(live.some((r) => r.status === "PROCESSING") ? "scan" : "review");
      setSel(0);
    } catch (err) {
      setConfirmError(err?.response?.data?.error || "Could not resume this import. Please retry.");
    }
  };

  const runDocUpload = async () => {
    const queue = files.map((file) => ({ file, status: "waiting" }));
    setDocQueue(queue);
    setStep("upload");

    let anySucceeded = false;
    for (let i = 0; i < files.length; i++) {
      setDocQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "uploading" } : item)));
      try {
        const fd = new FormData();
        fd.append("data", JSON.stringify({ title: titleFromFileName(files[i].name), contentType: "FILE", tags: [], required: false }));
        fd.append("file", files[i]);
        await createDocumentBankItem(fd);
        anySucceeded = true;
        setDocQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "done" } : item)));
      } catch (err) {
        setDocQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "failed", error: err?.response?.data?.error || err?.message || "Upload failed." } : item)));
      }
    }
    if (anySucceeded) onImported?.();
  };

  const goToReview = () => setStep("review");

  const handleSaveDrafts = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      const included = drafts.filter((d) => d.include);
      for (const d of included) {
        if (d.dirty) await updateGeminiImportRow(batchId, d.rowId, serializeDraft(d));
      }
      const data = await confirmGeminiImport(batchId, included.map((d) => d.rowId));
      const committed = data.committed || [];
      const skipped = data.skipped || [];
      const failures = data.failed || [];
      const savedIds = new Set([...committed, ...skipped].map((r) => r.rowId));
      // Compatibility with responses from older backends that only return a committed count.
      if (failures.length === 0 && skipped.length === 0 && committed.length === included.length) included.forEach((d) => savedIds.add(d.rowId));
      setDrafts((prev) => prev.filter((d) => !savedIds.has(d.rowId)));
      setSel(0);
      if (committed.length > 0 || skipped.length > 0) {
        setConfirmedCount((prev) => (prev || 0) + committed.length + skipped.length);
        onImported?.();
        // A file can have unfinished sibling scans even after every visible draft was saved.
        // Keep the resume link until the server confirms the entire batch is terminal.
        try {
          const rows = await listGeminiImportRows(batchId);
          if (Array.isArray(rows) && rows.every((r) => ["CONFIRMED", "DISCARDED"].includes(r.status))) {
            sessionStorage.removeItem(storageKey);
          }
        } catch { /* Preserve recovery metadata when status cannot be verified. */ }
      }
      if (failures.length > 0 || (committed.length === 0 && skipped.length === 0)) {
        setConfirmError(failures.length ? failures.map((f) => (included.find((d) => d.rowId === f.rowId)?.payload.title || "Sheet") + ": " + f.error).join("; ") : "No drafts were saved. Your drafts are retained; please retry.");
      }
    } catch (err) {
      setConfirmError(err?.response?.data?.error || err?.message || "Couldn't save these drafts.");
    } finally {
      setConfirming(false);
    }
  };

  const noTenant = !tenantId;
  const scanDone = scanQueue.length > 0 && scanQueue.every((q) => q.status !== "waiting" && q.status !== "uploading");
  const uploadDone = docQueue.length > 0 && docQueue.every((q) => q.status === "done" || q.status === "failed");
  const includedCount = drafts.filter((d) => d.include).length;
  const includedMissingInfoCount = drafts.filter((d) => d.include && computeMissingFields(d).length > 0).length;
  const unresolvedFlagCount = drafts.filter((d) => d.include).reduce((n, d) => n + countUnresolvedFlags(d), 0);

  const title = step === "review" ? "Review AI drafts" : step === "scan" ? (scanDone ? "Scan complete" : `Scanning ${files.length} file${files.length > 1 ? "s" : ""}`) : step === "upload" ? "Uploading to Document Library" : "Import to Knowledge Bank";
  const subtitle = step === "review" ? "Edit anything the AI got wrong. Confirm flagged items before saving."
    : step === "scan" ? (scanDone ? "Check the results, then review the drafts." : "Keep this open while your files are scanned.")
      : step === "upload" ? null : "Scan a skill sheet or test with AI, or store a document as-is.";
  const showAiChip = kind === "ai" && step !== "pick";

  return (
    <Dialog open={open} onClose={() => handleClose()} maxWidth={step === "review" || step === "scan" ? "md" : "sm"} fullWidth>
      <DialogTitle sx={{ pb: "14px", borderBottom: `1px solid ${W.line}` }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {title}
              {showAiChip && <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "8px", py: "2px", borderRadius: "999px", bgcolor: W.aiBg, color: W.ai, border: `1px solid ${W.aiLine}` }}>AI</Box>}
            </Box>
            {subtitle && <Box sx={{ fontSize: "13.5px", fontWeight: 400, color: W.ink3, mt: "4px" }}>{subtitle}</Box>}
          </Box>
          <Box component="button" type="button" aria-label="Close" onClick={() => handleClose(confirmedCount !== null ? true : undefined)}
            sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: "8px", border: `1px solid ${W.line}`, bgcolor: "#fff", color: W.ink2, cursor: "pointer", display: "grid", placeItems: "center", "&:hover": { bgcolor: W.surface2 } }}>
            <Box component="svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></Box>
          </Box>
        </Box>
        <Stepper kind={kind} step={step} />
      </DialogTitle>
      <DialogContent sx={{ pt: "18px !important" }}>
        {noTenant && <Alert severity="warning" sx={{ mb: 2 }}>No active tenant selected — pick a tenant before importing.</Alert>}

        {!noTenant && step === "pick" && resumeBatchId && <Alert severity="info" sx={{ mb: 2 }}>An unfinished import is available.<Button onClick={resumeScan}>Resume import</Button><Button onClick={() => { discardGeminiImport(resumeBatchId).catch(() => {}); sessionStorage.removeItem(storageKey); setResumeBatchId(null); }}>Discard import</Button></Alert>}
        {!noTenant && step === "pick" && (
          <PickStep kind={kind} setKind={setKind} aiType={aiType} setAiType={setAiType} files={files} setFiles={setFiles} />
        )}
        {!noTenant && step === "scan" && <ScanStep queue={scanQueue} aiType={aiType} onRetry={retryFile} />}
        {!noTenant && step === "review" && (
          <ReviewStep drafts={drafts} sel={sel} setSel={setSel} onChangeDraft={(i, patch) => setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)))} />
        )}
        {!noTenant && step === "upload" && <DocUploadStep queue={docQueue} />}

        {confirmError && <Alert severity="error" sx={{ mt: 2 }}>{confirmError}</Alert>}
        {confirmedCount !== null && <Alert severity="success" sx={{ mt: 2 }}>Saved {confirmedCount} draft{confirmedCount === 1 ? "" : "s"}. Publish them in Knowledge Bank when ready.</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${W.line}` }}>
        {step === "pick" && <Button onClick={() => handleClose()}>Cancel</Button>}
        {(step === "scan" || step === "upload") && confirmedCount === null && <Button onClick={() => handleClose()}>Close</Button>}

        {step === "pick" && (
          <Button variant="contained" disabled={noTenant || files.length === 0 || (kind === "ai" && Boolean(resumeBatchId))} onClick={kind === "ai" ? runAiScan : runDocUpload}>
            {kind === "ai" ? "Scan with AI" : "Upload"}
          </Button>
        )}
        {step === "scan" && (
          <Button variant="contained" disabled={!scanDone || drafts.length === 0} onClick={goToReview}>
            Review drafts
          </Button>
        )}
        {step === "review" && drafts.length > 0 && (
          <>
            <Box sx={{ fontSize: "12.5px", color: W.danger, mr: "auto" }}>
              {includedMissingInfoCount > 0
                ? `⚠ Fill in the missing info on ${includedMissingInfoCount === 1 ? "the flagged draft" : `${includedMissingInfoCount} flagged drafts`} before saving.`
                : unresolvedFlagCount > 0
                  ? `⚠ Confirm ${unresolvedFlagCount} flagged item${unresolvedFlagCount === 1 ? "" : "s"} to save.`
                  : null}
            </Box>
            <Button onClick={() => handleClose(undefined, true)}>Discard all</Button>
            <Button variant="contained" disabled={includedCount === 0 || includedMissingInfoCount > 0 || unresolvedFlagCount > 0 || confirming} onClick={handleSaveDrafts}>
              {confirming ? <CircularProgress size={20} /> : `Save ${includedCount} draft${includedCount === 1 ? "" : "s"}`}
            </Button>
          </>
        )}
        {(step === "review" || step === "upload") && confirmedCount !== null && (
          <Button variant="contained" onClick={() => handleClose(true)}>Done</Button>
        )}
        {step === "upload" && uploadDone && (
          <Button variant="contained" onClick={() => handleClose(true)}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
